import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Clock, PlayCircle, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  getPublicFilm,
  getPublicFilmEpisodes,
  type PublicFilm,
  type PublicEpisodesResponse,
} from '../../lib/api/public-films.client'

function formatPremiereDate(premiereAt?: string | null): string | null {
  if (!premiereAt) return null
  const date = new Date(premiereAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function ShowDetail() {
  const { filmId } = useParams<{ filmId: string }>()
  const [film, setFilm] = useState<PublicFilm | null>(null)
  const [episodes, setEpisodes] = useState<PublicEpisodesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!filmId) {
      setError('Missing show ID')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    Promise.all([getPublicFilm(filmId), getPublicFilmEpisodes(filmId)])
      .then(([f, e]) => {
        setFilm(f)
        setEpisodes(e)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load show')
        setFilm(null)
        setEpisodes(null)
      })
      .finally(() => setLoading(false))
  }, [filmId])

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Loading show...</div>
      </div>
    )
  }

  if (error || !film) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-gray-400">{error || 'Show not found'}</p>
        <Link to="/">
          <Button variant="secondary" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Button>
        </Link>
      </div>
    )
  }

  const heroImage = film.coverImage || film.thumbnailUrl || ''
  const episodesData = episodes ?? { episodes: [], showId: film.id, showTitle: film.title }

  return (
    <div className="min-h-screen bg-black text-white">
      <section className="relative max-w-6xl mx-auto px-6 pb-16 pt-6 md:pt-10">
        {heroImage && (
          <div
            className="absolute inset-0 -z-10 opacity-40"
            style={{
              backgroundImage: `url(${heroImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(12px)',
            }}
          />
        )}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black via-black/80 to-black" />

        <header className="mb-8 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-300 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>
          <span className="inline-flex items-center gap-2 text-xs md:text-sm text-gray-400">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            Series overview – no playback on web
          </span>
        </header>

        <div className="grid md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.1fr)] gap-8 md:gap-12 items-start mb-12">
          <div className="space-y-5">
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/40 shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={film.title}
                  className="aspect-[9/14] w-full object-contain"
                />
              ) : (
                <div className="aspect-[9/14] w-full bg-gradient-to-br from-[#4A1FB8] to-[#D72638] flex items-center justify-center">
                  <PlayCircle className="w-12 h-12 text-white/60" />
                </div>
              )}
            </div>

            <div className="space-y-3">
              <p className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-medium text-[#FFD700]">
                Binyuma TV Series
              </p>
              <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                {film.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-gray-300">
                {film.genre && (
                  <span className="rounded-full bg-white/5 px-3 py-1">{film.genre}</span>
                )}
                {film.language && (
                  <span className="rounded-full bg-white/5 px-3 py-1">{film.language}</span>
                )}
                {film.releaseYear != null && (
                  <span className="rounded-full bg-white/5 px-3 py-1">{film.releaseYear}</span>
                )}
                {typeof film.episodeCount === 'number' && film.episodeCount > 0 && (
                  <span className="rounded-full bg-white/5 px-3 py-1">
                    {film.episodeCount} episodes
                  </span>
                )}
              </div>
              {film.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {film.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-white/5 px-3 py-1 text-xs text-gray-300"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {film.synopsis && (
              <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                {film.synopsis}
              </p>
            )}

            <div className="rounded-2xl border border-[#4A1FB8]/40 bg-black/60 p-4 md:p-5 space-y-3">
              <h2 className="text-sm font-semibold text-gray-100">
                Watch this show in the app
              </h2>
              <p className="text-xs md:text-sm text-gray-400">
                Show details on this page are informational only. To actually watch
                episodes, download the Binyuma TV app on your device.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/download">
                  <Button className="bg-[#D72638] hover:bg-[#D72638]/90 text-white text-sm">
                    Download Binyuma TV
                  </Button>
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/60 p-4 md:p-5 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  <h2 className="text-sm font-semibold text-gray-100">
                    Episodes & premieres
                  </h2>
                </div>
                {episodesData.nextPremiereAt && (
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">
                      Next premiere
                    </p>
                    <p className="text-xs text-[#D4AF37] font-semibold">
                      {formatPremiereDate(episodesData.nextPremiereAt)}
                    </p>
                  </div>
                )}
              </div>

              {!episodesData.episodes?.length ? (
                <p className="text-xs md:text-sm text-gray-400">
                  Episodes for this show will appear here once they are scheduled.
                </p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {[...(episodesData.episodes ?? [])]
                    .sort((a, b) => (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0))
                    .map((ep) => {
                    const premiereLabel = formatPremiereDate(ep.premiereAt)
                    const isFuturePremiere =
                      ep.premiereAt &&
                      !Number.isNaN(new Date(ep.premiereAt).getTime()) &&
                      new Date(ep.premiereAt).getTime() > Date.now()
                    return (
                      <li key={ep.id} className="py-3 flex gap-3">
                        <div className="w-16 h-24 flex-shrink-0 overflow-hidden rounded-xl bg-gray-900">
                          {ep.thumbnailUrl ? (
                            <img
                              src={ep.thumbnailUrl}
                              alt={ep.title}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#4A1FB8] to-[#D72638]">
                              <PlayCircle className="w-6 h-6 text-white/70" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-[11px] text-gray-400 mb-1">
                            {typeof ep.episodeNumber === 'number' && (
                              <span className="font-semibold">Episode {ep.episodeNumber}</span>
                            )}
                            {ep.seasonNumber != null && (
                              <span className="rounded-full bg-white/5 px-2 py-0.5">
                                Season {ep.seasonNumber}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-semibold text-gray-100">{ep.title}</p>
                          {ep.description && (
                            <p className="mt-1 text-xs text-gray-400 line-clamp-2">
                              {ep.description}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                            {typeof ep.duration === 'number' && ep.duration > 0 && (
                              <span>{Math.max(1, Math.round(ep.duration / 60))} min</span>
                            )}
                            {premiereLabel && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-0.5">
                                <Clock className="w-3 h-3" />
                                {isFuturePremiere ? 'Premieres' : 'Premiered'} {premiereLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
